import Analytics from "pages/dashboard/analytics";
import AuthService from "api/services/AuthService";
import Campaigns from "pages/dashboard/campaigns";
import Customers from "pages/dashboard/customers";
import DataTraining from "pages/dashboard/data-training";
import Announcements from "pages/dashboard/setting/announcements";
import Faqs from "pages/dashboard/setting/faqs";
import MainMenu from "pages/dashboard/setting/mainmenu";
import ErrorPage404 from "components/ErrorPage404";
import { PrivateRoute } from "components/PrivateRoute";
import React, { useEffect } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import FaqList from "pages/dashboard/setting/faqs/faq/FaqList";
import AddMenuOption from "pages/dashboard/setting/mainmenu/AddMenuOption";
import EditMenuOption from "pages/dashboard/setting/mainmenu/EditMenuOption";
import Roles from "pages/dashboard/setting/roles";
import TeamUsers from "pages/dashboard/setting/team-users";
import PropTypes from 'prop-types';

export const APP_ROUTES = [
    {
        path: "/analytics",
        _key: "analytics",
        exact: true,
        permission: 'analytics:view',
    },
    {
        path: "/users",
        _key: "customers",
        exact: true,
        permission: 'users:view',
    },
    {
        path: "/campaigns",
        _key: "campaigns",
        exact: true,
        permission: 'campaigns:view',
    },
    {
        path: "/data-training",
        _key: "data_training",
        exact: true,
        permission: 'data-training:view',
    },
    {
        path: "/settings",
        _key: "settings",
        exact: true,
        // No permission key — sidebar shows this group if user has access to any child
        routes: [
            {
                path: "/settings/faqs",
                _key: "faqs",
                exact: true,
                permission: 'faqs:manage',
            },
            {
                path: "/settings/mainmenu",
                _key: "main_menu",
                exact: true,
                permission: 'mainmenu:manage',
            },
            {
                path: "/settings/announcements",
                _key: "announcements",
                exact: true,
                permission: 'announcements:manage',
            },
            {
                path: "/settings/roles",
                _key: "roles",
                exact: true,
                permission: 'roles:manage',
            },
            {
                path: "/settings/team-users",
                _key: "team_users",
                exact: true,
                permission: 'team:manage',
            },
        ]
    }
];

export const side_bar_group = [
    {
        to: '/analytics',
        _key: "analytics",
        permission: 'analytics:view',
    },
    {
        to: "/users",
        _key: "customers",
        permission: 'users:view',
    },
    {
        to: "/campaigns",
        _key: "campaigns",
        permission: 'campaigns:view',
    },
    {
        to: "/data-training",
        _key: "data_training",
        permission: 'data-training:view',
    },
    {
        to: "/settings",
        _key: 'settings',
        // No permission key — sidebar shows this group if user has access to any child
        routes: [
            {
                to: "/settings/faqs",
                _key: 'faqs',
                permission: 'faqs:manage',
            },
            {
                to: "/settings/mainmenu",
                _key: 'main_menu',
                permission: 'mainmenu:manage',
            },
            {
                to: "/settings/announcements",
                _key: 'announcements',
                permission: 'announcements:manage',
            },
            {
                to: "/settings/roles",
                _key: 'roles',
                permission: 'roles:manage',
            },
            {
                to: "/settings/team-users",
                _key: 'team_users',
                permission: 'team:manage',
            },
        ],
    }
];

const ORDERED_ROUTES = [
    { path: '/analytics',             permission: 'analytics:view' },
    { path: '/users',                 permission: 'users:view' },
    { path: '/campaigns',             permission: 'campaigns:view' },
    { path: '/data-training',         permission: 'data-training:view' },
    { path: '/settings/faqs',         permission: 'faqs:manage' },
    { path: '/settings/mainmenu',     permission: 'mainmenu:manage' },
    { path: '/settings/announcements',permission: 'announcements:manage' },
    { path: '/settings/roles',        permission: 'roles:manage' },
    { path: '/settings/team-users',   permission: 'team:manage' },
];

function getDefaultRoute() {
    const first = ORDERED_ROUTES.find(r => AuthService.hasPermission(r.permission));
    return first?.path ?? '/login';
}

/**
 * Render a route with potential sub routes
 */
function RouteWithSubRoutes(route) {
    return (
        <PrivateRoute
            path={route.path}
            exact={route.exact}
            permission={route.permission}
            component={props => <route.component {...props} routes={route.routes} />}
        />
    );
}

/**
 * Use this component for any new section of routes (any config object that has a "routes" property
 */
export function RenderRoutes({ routes }) {
    return (
        <Switch>
            {routes.map((route, i) => {
                return <RouteWithSubRoutes key={route.key} {...route} />;
            })}
            <Route component={ErrorPage404} />
        </Switch>
    );
}

RenderRoutes.propTypes = {
    routes: PropTypes.array,
};

export function DashboardRouter() {
    useEffect(() => {}, [])
    return (
        <Switch>
            <Redirect exact from='/' to={getDefaultRoute()} />
            <PrivateRoute exact path="/analytics"                      permission="analytics:view"        component={Analytics} />
            <PrivateRoute exact path="/campaigns"                      permission="campaigns:view"        component={Campaigns} />
            <PrivateRoute exact path="/data-training"                  permission="data-training:view"   component={DataTraining} />
            <PrivateRoute exact path="/users"                          permission="users:view"            component={Customers} />

            <Redirect exact from='/settings' to={getDefaultRoute()} />
            <PrivateRoute exact path="/settings/faqs"                  permission="faqs:manage"          component={Faqs} />
            <PrivateRoute exact path="/settings/faqs/:category_id/:id" permission="faqs:manage"          component={FaqList} />
            <PrivateRoute exact path="/settings/mainmenu"              permission="mainmenu:manage"      component={MainMenu} />
            <PrivateRoute exact path="/settings/announcements"         permission="announcements:manage"  component={Announcements} />
            <PrivateRoute exact path="/settings/mainmenu/new"          permission="mainmenu:manage"      component={AddMenuOption} />
            <PrivateRoute exact path="/settings/mainmenu/edit/:id"     permission="mainmenu:manage"      component={EditMenuOption} />

            <PrivateRoute exact path="/settings/roles"               permission="roles:manage"         component={Roles} />
            <PrivateRoute exact path="/settings/team-users"         permission="team:manage"           component={TeamUsers} />

            <Route component={ErrorPage404} />
        </Switch>
    )
}
